/**
 * Address Form Field sub-components for AddressModal.
 */

import { Input, Select, FormField, CountrySelect, StateSelect, Checkbox, Button, QuickPasteAddress } from '../../ui'
import { CheckboxGroup } from '../../ui/checkbox-group'

import type { Address } from '../../../types/billing'

// ── Quick-paste bar ─────────────────────────────────────────────

interface QuickPasteBarProps {
  id: string
  countryCode: string
  onChange: (field: keyof Address, value: string) => void
}

export function AddressQuickPasteBar({ id, countryCode, onChange }: QuickPasteBarProps): JSX.Element {
  return (
    <QuickPasteAddress
      id={id}
      includeGSTIN={countryCode === 'IN'}
      onParse={(parsed) => {
        if (parsed.line1) { onChange('line1', parsed.line1) }
        if (parsed.line2) { onChange('line2', parsed.line2) }
        if (parsed.city) { onChange('city', parsed.city) }
        if (parsed.state) { onChange('state', parsed.state) }
        if (parsed.stateCode) { onChange('stateCode', parsed.stateCode) }
        if (parsed.postalCode) { onChange('postalCode', parsed.postalCode) }
        if (parsed.gstin) { onChange('gstin', parsed.gstin) }
      }}
    />
  )
}

/** Physical address types — WHAT kind of place this is */
export const ADDRESS_TYPES = [
  { value: 'office', label: 'Office' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'residential', label: 'Residential' },
  { value: 'other', label: 'Other' },
]

/** Address usages — HOW this address is used (multi-select) */
export const ADDRESS_USAGES = [
  { value: 'billing', label: 'Billing' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'consignee', label: 'Consignee' },
]

// ── Label + Type + Usage row ────────────────────────────────────

interface LabelTypeFieldsProps {
  label: string
  type: string
  usage?: string[]
  labelError?: string
  typeError?: string
  onChange: (field: keyof Address, value: string) => void
  onUsageChange?: (values: string[]) => void
}

export function AddressLabelTypeFields({ label, type, usage, labelError, typeError, onChange, onUsageChange }: LabelTypeFieldsProps): JSX.Element {
  return (
    <>
      <div className="grid grid-cols-2 el-gap-field">
        <FormField required error={labelError} label="Address Label">
          <Input
            hideMessage
            placeholder="e.g., Head Office, Warehouse 1"
            value={label ?? ''}
            onChange={(e) => onChange('label', e.target.value)}
          />
        </FormField>
        <FormField required error={typeError} label="Address Type">
          <Select
            hideMessage
            options={ADDRESS_TYPES}
            value={type ?? ''}
            onChange={(value) => onChange('type', value)}
          />
        </FormField>
      </div>
      {onUsageChange && (
        <FormField label="Used for">
          <div className="el-bleed">
            <CheckboxGroup
              layout="horizontal"
              name="address-usage"
              options={ADDRESS_USAGES}
              size="sm"
              value={usage ?? []}
              onChange={onUsageChange}
            />
          </div>
        </FormField>
      )}
    </>
  )
}

// ── Address lines ───────────────────────────────────────────────

interface AddressLineFieldsProps {
  line1: string
  line2: string
  line1Error?: string
  onChange: (field: keyof Address, value: string) => void
}

export function AddressLineFields({ line1, line2, line1Error, onChange }: AddressLineFieldsProps): JSX.Element {
  return (
    <>
      <FormField required error={line1Error} label="Address Line 1">
        <Input
          hideMessage
          placeholder="Street address"
          value={line1 ?? ''}
          onChange={(e) => onChange('line1', e.target.value)}
        />
      </FormField>
      <FormField label="Address Line 2">
        <Input
          hideMessage
          placeholder="Apartment, suite, etc. (optional)"
          value={line2 ?? ''}
          onChange={(e) => onChange('line2', e.target.value)}
        />
      </FormField>
    </>
  )
}

// ── Country + State ─────────────────────────────────────────────

interface CountryStateFieldsProps {
  countryCode: string
  stateCode: string
  stateFieldLabel: string
  countryError?: string
  stateError?: string
  hasStates: boolean
  onCountryChange: (code: string | null, name: string | null) => void
  onStateChange: (code: string | null, name: string | null, fullIdentifier?: string | null) => void
}

export function AddressCountryStateFields({
  countryCode,
  stateCode,
  stateFieldLabel,
  countryError,
  stateError,
  hasStates,
  onCountryChange,
  onStateChange,
}: CountryStateFieldsProps): JSX.Element {
  return (
    <>
      <div className="space-y-1">
        <label className="block text-sm font-medium text-foreground" htmlFor="address-modal-country">
          Country <span className="text-red-500">*</span>
        </label>
        <CountrySelect
          error={countryError}
          id="address-modal-country"
          placeholder="Select country"
          value={countryCode ?? ''}
          onChange={onCountryChange}
        />
      </div>

      {hasStates && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-2" htmlFor="address-modal-state">
            {stateFieldLabel}
          </label>
          <StateSelect
            countryCode={countryCode ?? ''}
            error={stateError}
            id="address-modal-state"
            placeholder={`Select ${stateFieldLabel}`}
            value={stateCode ?? ''}
            onChange={onStateChange}
          />
        </div>
      )}
    </>
  )
}

// ── City + Postal Code ──────────────────────────────────────────

interface CityPostalFieldsProps {
  city: string
  postalCode: string
  cityError?: string
  postalCodeError?: string
  postalCodeLabel: string
  postalCodeExample: string
  onChange: (field: keyof Address, value: string) => void
}

export function AddressCityPostalFields({
  city,
  postalCode,
  cityError,
  postalCodeError,
  postalCodeLabel,
  postalCodeExample,
  onChange,
}: CityPostalFieldsProps): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 el-gap-field">
      <div>
        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="address-modal-city">
          City
        </label>
        <Input
          required
          error={cityError}
          id="address-modal-city"
          placeholder="City"
          value={city ?? ''}
          onChange={(e) => onChange('city', e.target.value)}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-foreground mb-2" htmlFor="address-modal-postal-code">
          {postalCodeLabel}
        </label>
        <Input
          required
          error={postalCodeError}
          id="address-modal-postal-code"
          placeholder={postalCodeExample}
          value={postalCode ?? ''}
          onChange={(e) => onChange('postalCode', e.target.value)}
        />
      </div>
    </div>
  )
}

// ── India-specific fields (GSTIN, GST State Code, Company Code) ─

interface IndiaFieldsProps {
  gstin: string
  stateCode: string
  companyCode: string
  hasStates: boolean
  onChange: (field: keyof Address, value: string) => void
}

export function AddressIndiaFields({ gstin, stateCode, companyCode, hasStates, onChange }: IndiaFieldsProps): JSX.Element {
  return (
    <>
      <div className="grid grid-cols-2 el-gap-field">
        <FormField helperText="For addresses with GST registration" label="GSTIN">
          <Input
            hideMessage
            placeholder="GST Identification Number (optional)"
            value={gstin ?? ''}
            onChange={(e) => onChange('gstin', e.target.value)}
          />
        </FormField>
        <FormField helperText="Optional override for GST state code" label="GST State Code">
          <Input
            hideMessage
            disabled={hasStates}
            placeholder="e.g., 07 for Delhi"
            value={stateCode ?? ''}
            onChange={(e) => onChange('stateCode', e.target.value)}
          />
        </FormField>
      </div>
      <FormField helperText="Customer's internal code for this location" label="Company Code">
        <Input
          hideMessage
          maxLength={50}
          placeholder="e.g., COMP-MUM-01"
          value={companyCode ?? ''}
          onChange={(e) => onChange('companyCode', e.target.value)}
        />
      </FormField>
    </>
  )
}

// ── Modal action buttons ────────────────────────────────────────

interface AddressModalActionsProps {
  isEditing: boolean
  onCancel: () => void
  onSave: () => void
}

export function AddressModalActions({ isEditing, onCancel, onSave }: AddressModalActionsProps): JSX.Element {
  return (
    <div className="flex justify-end el-gap-field pt-4 border-t border-border">
      <Button size="sm" type="button" variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button size="sm" type="button" variant="primary" onClick={onSave}>
        {isEditing ? 'Save Changes' : 'Add Address'}
      </Button>
    </div>
  )
}

// ── Primary checkbox ────────────────────────────────────────────

interface PrimaryCheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
}

export function AddressPrimaryCheckbox({ checked, onChange }: PrimaryCheckboxProps): JSX.Element {
  return (
    <Checkbox
      hideMessage
      checked={checked}
      label="Set as primary address"
      onChange={onChange}
    />
  )
}
